import { Catalog, type Entries, type Language, languages } from './catalog';
import { en } from './en';
import { es } from './es';

const catalogs: Record<Language, Entries> = { en, es };

/**
 * Which language the interface is in, how it is chosen, and how dates and numbers are written in it.
 *
 * The order of preference is deliberate: what the account asked for, then what this browser remembered,
 * then what the browser reports, then English. The account comes first so the choice follows a person
 * between devices; the browser's own setting is only a starting guess and never overrides a choice.
 *
 * This class holds the catalog and nothing else about the choice: the device copy and the document's
 * `lang` belong to the family's `Preferences` (`@accounts/theme`), which the session keeps in step.
 *
 * A language also survives the trip to a product and back, carried as `?lang=` on a return destination
 * and validated on arrival, because returning from signing in and finding the interface in another
 * language is the sort of small failure that makes people distrust the whole flow.
 */
export class Selection {
	static PARAMETER = 'lang';

	#language: Language;
	get language() {
		return this.#language;
	}

	#catalog: Catalog;
	get catalog() {
		return this.#catalog;
	}

	#listeners = new Set<(language: Language) => void>();

	constructor(language: Language = Selection.preferred()) {
		this.#language = language;
		this.#catalog = new Catalog(language, catalogs[language], en);
	}

	/**
	 * @returns A supported language, or undefined for anything else. Nothing is coerced: an
	 * unsupported value means "no preference stated", not "use the closest".
	 */
	static supported(value: unknown): Language | undefined {
		return languages.includes(value as Language) ? (value as Language) : undefined;
	}

	/**
	 * What to start in, before an account has been read
	 *
	 * @param remembered The language this browser's copy holds, when it holds one
	 */
	static preferred(remembered?: string): Language {
		const search = globalThis.location ? new URLSearchParams(globalThis.location.search) : undefined;
		const carried = Selection.supported(search?.get(Selection.PARAMETER));
		if (carried) return carried;

		const kept = Selection.supported(remembered);
		if (kept) return kept;

		for (const candidate of globalThis.navigator?.languages ?? []) {
			const language = Selection.supported(candidate.split('-')[0]);
			if (language) return language;
		}
		return 'en';
	}

	select(language: Language): void {
		if (language === this.#language) return;
		this.#language = language;
		this.#catalog = new Catalog(language, catalogs[language], en);
		for (const listener of this.#listeners) listener(language);
	}

	listen(listener: (language: Language) => void): () => void {
		this.#listeners.add(listener);
		return () => this.#listeners.delete(listener);
	}

	/**
	 * Adds this language to a destination, so a product opens in the language the person is reading
	 */
	carry(destination: string): string {
		try {
			const url = new URL(destination);
			url.searchParams.set(Selection.PARAMETER, this.#language);
			return url.toString();
		} catch {
			return destination;
		}
	}

	/**
	 * A date, written the way this language writes dates
	 */
	date(value: string | Date | undefined): string {
		if (!value) return '—';
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return '—';
		return new Intl.DateTimeFormat(this.#language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
	}

	number(value: number): string {
		return new Intl.NumberFormat(this.#language).format(value);
	}
}
